import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { 
  FileSearch, 
  TrendingUp, 
  Shield, 
  FileText, 
  Send, 
  Loader2,
  AlertTriangle,
  CheckCircle,
  Mail
} from "lucide-react";

const agents = {
  marketing: {
    title: "Marketing Review Agent",
    description: "Submit marketing materials for AI-powered compliance analysis. The agent reviews content as a Chief Compliance Officer and SEC examiner would.",
    icon: FileSearch,
    color: "text-blue-700",
    bgColor: "bg-blue-50",
  },
  research: {
    title: "Research Agent",
    description: "Get AI-powered research reports on companies for due diligence purposes. Includes company overview, financial health, and risk factors.",
    icon: TrendingUp,
    color: "text-emerald-700",
    bgColor: "bg-emerald-50",
  },
  "sec-compliance": {
    title: "SEC Compliance Monitor",
    description: "Analyze content for SEC compliance violations including promissory language, testimonial issues, and missing disclosures.",
    icon: Shield,
    color: "text-purple-700",
    bgColor: "bg-purple-50",
  },
  "form-adv": {
    title: "Form ADV / Regulatory Agent",
    description: "Get updates on SEC regulations, state-level changes, and Form ADV amendment suggestions.",
    icon: FileText,
    color: "text-amber-700",
    bgColor: "bg-amber-50",
  },
};

function MarketingReviewForm({ onSubmit, loading }) {
  const [content, setContent] = useState("");
  const [contentType, setContentType] = useState("marketing_material");
  const [sendEmail, setSendEmail] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ content, content_type: contentType, send_email: sendEmail });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="contentType">Content Type</Label>
        <Select value={contentType} onValueChange={setContentType}>
          <SelectTrigger data-testid="content-type-select">
            <SelectValue placeholder="Select content type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="marketing_material">Marketing Material</SelectItem>
            <SelectItem value="email">Email Communication</SelectItem>
            <SelectItem value="website">Website Content</SelectItem>
            <SelectItem value="social_media">Social Media Post</SelectItem>
            <SelectItem value="advertisement">Advertisement</SelectItem>
            <SelectItem value="presentation">Presentation</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="content">Content to Review</Label>
        <Textarea
          id="content"
          placeholder="Paste your marketing content here..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="min-h-[200px] input-focus"
          data-testid="marketing-content-input"
          required
        />
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox 
          id="sendEmail" 
          checked={sendEmail} 
          onCheckedChange={setSendEmail}
          data-testid="send-email-checkbox"
        />
        <Label htmlFor="sendEmail" className="text-sm">
          Send analysis to compliance team (pointecapital@complisun.com)
        </Label>
      </div>

      <Button type="submit" className="btn-primary w-full" disabled={loading || !content.trim()} data-testid="submit-marketing-review">
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
        {loading ? "Analyzing..." : "Submit for Review"}
      </Button>
    </form>
  );
}

function ResearchForm({ onSubmit, loading }) {
  const [companyName, setCompanyName] = useState("");
  const [tickerSymbol, setTickerSymbol] = useState("");
  const [researchFocus, setResearchFocus] = useState("general");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ company_name: companyName, ticker_symbol: tickerSymbol || null, research_focus: researchFocus });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="companyName">Company Name</Label>
        <Input
          id="companyName"
          placeholder="e.g., Apple Inc."
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          className="input-focus"
          data-testid="company-name-input"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="tickerSymbol">Ticker Symbol (Optional)</Label>
        <Input
          id="tickerSymbol"
          placeholder="e.g., AAPL"
          value={tickerSymbol}
          onChange={(e) => setTickerSymbol(e.target.value.toUpperCase())}
          className="input-focus"
          data-testid="ticker-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="researchFocus">Research Focus</Label>
        <Select value={researchFocus} onValueChange={setResearchFocus}>
          <SelectTrigger data-testid="research-focus-select">
            <SelectValue placeholder="Select focus area" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="general">General Overview</SelectItem>
            <SelectItem value="financial">Financial Analysis</SelectItem>
            <SelectItem value="compliance">Compliance History</SelectItem>
            <SelectItem value="risk">Risk Assessment</SelectItem>
            <SelectItem value="news">Recent News</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Button type="submit" className="btn-primary w-full" disabled={loading || !companyName.trim()} data-testid="submit-research">
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TrendingUp className="h-4 w-4 mr-2" />}
        {loading ? "Researching..." : "Generate Research Report"}
      </Button>
    </form>
  );
}

function SECComplianceForm({ onSubmit, loading }) {
  const [content, setContent] = useState("");
  const [contentType, setContentType] = useState("website");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ content, content_type: contentType });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="secContentType">Content Type</Label>
        <Select value={contentType} onValueChange={setContentType}>
          <SelectTrigger data-testid="sec-content-type-select">
            <SelectValue placeholder="Select content type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="website">Website Content</SelectItem>
            <SelectItem value="email">Email Communication</SelectItem>
            <SelectItem value="marketing">Marketing Material</SelectItem>
            <SelectItem value="social_media">Social Media</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="secContent">Content to Analyze</Label>
        <Textarea
          id="secContent"
          placeholder="Paste the content you want to check for SEC compliance..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="min-h-[200px] input-focus"
          data-testid="sec-content-input"
          required
        />
      </div>

      <Button type="submit" className="btn-primary w-full" disabled={loading || !content.trim()} data-testid="submit-sec-compliance">
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
        {loading ? "Analyzing..." : "Check SEC Compliance"}
      </Button>
    </form>
  );
}

function FormADVForm({ onSubmit, loading }) {
  const [query, setQuery] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ query: query || "What are the latest SEC regulatory updates and Form ADV requirements for RIAs?" });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="advQuery">Query (Optional)</Label>
        <Textarea
          id="advQuery"
          placeholder="Ask about SEC updates, Form ADV requirements, or leave blank for general updates..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="min-h-[100px] input-focus"
          data-testid="adv-query-input"
        />
      </div>

      <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="submit-form-adv">
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
        {loading ? "Fetching Updates..." : "Get Regulatory Updates"}
      </Button>
    </form>
  );
}

function AIResponse({ response }) {
  if (!response) return null;

  return (
    <Card className="border-green-200 bg-green-50/30" data-testid="ai-response-card">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <CardTitle className="text-lg" data-testid="analysis-complete-title">Analysis Complete</CardTitle>
        </div>
        {response.agent_type && (
          <p className="text-sm text-slate-500 mt-1">Agent: {response.agent_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
        )}
      </CardHeader>
      <CardContent>
        <div className="prose prose-sm max-w-none">
          <div 
            className="whitespace-pre-wrap text-sm bg-white p-4 border border-slate-200 overflow-auto max-h-[500px] font-mono"
            data-testid="ai-response-content"
          >
            {response.response}
          </div>
        </div>
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 text-xs text-amber-800">
          <strong>Disclaimer:</strong> {response.disclaimer}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AgentsPage() {
  const { agentType } = useParams();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const activeTab = agentType || "marketing";

  const handleSubmit = async (endpoint, data) => {
    setLoading(true);
    setResponse(null);

    try {
      console.log("Submitting to:", endpoint, data);
      const res = await api.post(endpoint, data);
      console.log("Response received:", res.data);
      setResponse(res.data);
      toast.success("Analysis complete!");
    } catch (error) {
      console.error("API Error:", error);
      const errorMsg = error.response?.data?.detail || "Analysis failed. Please try again.";
      toast.error(errorMsg);
      // Don't redirect on AI agent errors
      if (error.response?.status !== 401) {
        setResponse({ 
          response: `Error: ${errorMsg}`, 
          disclaimer: "Please try again or contact support." 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="agents-page">
        {/* Header */}
        <div>
          <p className="label-text mb-2">AI Agents</p>
          <h1 className="heading-xl">Compliance AI Agents</h1>
          <p className="body-text mt-2">
            Intelligent tools to analyze marketing materials, research companies, and monitor SEC compliance.
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 h-auto gap-2 bg-transparent p-0">
            {Object.entries(agents).map(([key, agent]) => {
              const Icon = agent.icon;
              return (
                <TabsTrigger
                  key={key}
                  value={key}
                  asChild
                  className="data-[state=active]:bg-white data-[state=active]:border-[#8B0000] data-[state=active]:text-[#8B0000] border border-slate-200 p-3 h-auto"
                >
                  <Link to={`/agents/${key}`} className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{agent.title.split(" ")[0]}</span>
                  </Link>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {Object.entries(agents).map(([key, agent]) => {
            const Icon = agent.icon;
            return (
              <TabsContent key={key} value={key} className="mt-6">
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Form Card */}
                  <Card className="border-slate-200">
                    <CardHeader>
                      <div className={`w-12 h-12 rounded ${agent.bgColor} flex items-center justify-center mb-2`}>
                        <Icon className={`h-6 w-6 ${agent.color}`} />
                      </div>
                      <CardTitle>{agent.title}</CardTitle>
                      <CardDescription>{agent.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {key === "marketing" && (
                        <MarketingReviewForm 
                          onSubmit={(data) => handleSubmit("/agents/marketing-review", data)} 
                          loading={loading} 
                        />
                      )}
                      {key === "research" && (
                        <ResearchForm 
                          onSubmit={(data) => handleSubmit("/agents/research", data)} 
                          loading={loading} 
                        />
                      )}
                      {key === "sec-compliance" && (
                        <SECComplianceForm 
                          onSubmit={(data) => handleSubmit("/agents/sec-compliance", data)} 
                          loading={loading} 
                        />
                      )}
                      {key === "form-adv" && (
                        <FormADVForm 
                          onSubmit={(data) => handleSubmit("/agents/form-adv", data)} 
                          loading={loading} 
                        />
                      )}
                    </CardContent>
                  </Card>

                  {/* Response Card */}
                  <div className="lg:sticky lg:top-24">
                    {loading ? (
                      <Card className="border-slate-200 bg-slate-50 min-h-[300px] flex items-center justify-center">
                        <CardContent className="text-center">
                          <Loader2 className="h-12 w-12 mx-auto mb-4 text-[#8B0000] animate-spin" />
                          <p className="text-slate-600 font-medium">Analyzing with AI...</p>
                          <p className="text-sm text-slate-500 mt-1">This may take a few moments</p>
                        </CardContent>
                      </Card>
                    ) : response ? (
                      <AIResponse response={response} />
                    ) : (
                      <Card className="border-slate-200 bg-slate-50 min-h-[300px] flex items-center justify-center">
                        <CardContent className="text-center text-slate-500">
                          <Icon className="h-12 w-12 mx-auto mb-4 opacity-30" />
                          <p>Submit a request to see AI analysis results here</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>

                {/* Warning Banner */}
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold">Important Notice</p>
                    <p>
                      AI-generated analysis is for informational purposes only and should not replace professional 
                      compliance advice. Always verify findings and consult with Chief Compliance Officer Colin Darke 
                      at <a href="mailto:colindarke@complisun.com" className="underline">colindarke@complisun.com</a> for 
                      final compliance decisions.
                    </p>
                  </div>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
    </Layout>
  );
}
